import React from 'react';
import { Box, Typography, Grid, alpha, useTheme } from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { Link } from 'react-router-dom';
import { getIdeLanguageSeo } from '../common/ideLanguageSeo';
import { COMPILER_LANGUAGES } from '../common/compilerLanguages';

const ACCENT = '#137274';

const IdeLanguageSeoSection = ({ languageId, compact = false }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const border = isDark ? '#30363d' : 'rgba(15, 60, 70, 0.1)';
  const muted = isDark ? '#8b949e' : '#5f6b73';
  const seo = getIdeLanguageSeo(languageId);

  const otherLangs = COMPILER_LANGUAGES.filter((l) => l.id !== languageId).slice(0, 6);

  return (
    <Box
      component="section"
      sx={{ maxWidth: 1280, mx: 'auto', mt: compact ? 2 : 3, mb: 2 }}
      aria-label={`${seo.h1} information`}
    >
      <Typography
        component="h1"
        sx={{
          fontWeight: 900,
          fontSize: { xs: '1.35rem', sm: '1.65rem' },
          letterSpacing: '-0.03em',
          mb: 1,
        }}
      >
        {seo.h1}
      </Typography>
      <Typography
        component="p"
        sx={{ color: muted, lineHeight: 1.6, fontSize: '0.95rem', maxWidth: 720, mb: 2.5 }}
      >
        {seo.intro}
      </Typography>

      <Grid container spacing={2} sx={{ mb: compact ? 2 : 3 }}>
        <Grid item xs={12} md={6}>
          <Box
            sx={{
              p: 2,
              height: '100%',
              borderRadius: 2.5,
              border: `1px solid ${border}`,
              bgcolor: isDark ? '#161b22' : '#fff',
            }}
          >
            <Typography sx={{ fontWeight: 800, fontSize: '0.95rem', mb: 1.25 }}>
              Why use CrafterHack IDE
            </Typography>
            {seo.features.map((item) => (
              <Box key={item} sx={{ display: 'flex', gap: 1, mb: 0.85, alignItems: 'flex-start' }}>
                <CheckCircleOutlineIcon sx={{ fontSize: 17, color: ACCENT, mt: 0.15, flexShrink: 0 }} />
                <Typography variant="body2" sx={{ color: muted, fontSize: '0.85rem', lineHeight: 1.45 }}>
                  {item}
                </Typography>
              </Box>
            ))}
          </Box>
        </Grid>
        <Grid item xs={12} md={6}>
          <Box
            sx={{
              p: 2,
              height: '100%',
              borderRadius: 2.5,
              border: `1px solid ${border}`,
              bgcolor: isDark ? alpha('#fff', 0.02) : alpha(ACCENT, 0.03),
            }}
          >
            <Typography sx={{ fontWeight: 800, fontSize: '0.95rem', mb: 1.25 }}>
              FAQ
            </Typography>
            {seo.faq.map((item) => (
              <Box key={item.q} sx={{ mb: 1.5 }}>
                <Typography sx={{ fontWeight: 700, fontSize: '0.85rem', mb: 0.35 }}>
                  {item.q}
                </Typography>
                <Typography variant="body2" sx={{ color: muted, fontSize: '0.82rem', lineHeight: 1.45 }}>
                  {item.a}
                </Typography>
              </Box>
            ))}
          </Box>
        </Grid>
      </Grid>

      {!compact && (
        <Box>
          <Typography sx={{ fontWeight: 800, fontSize: '0.85rem', mb: 1, color: muted }}>
            Other online compilers
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
            {otherLangs.map((lang) => (
              <Box
                key={lang.id}
                component={Link}
                to={`/ide/${lang.id}`}
                sx={{
                  px: 1.25,
                  py: 0.5,
                  borderRadius: 1.5,
                  fontSize: '0.8rem',
                  fontWeight: 650,
                  textDecoration: 'none',
                  color: ACCENT,
                  border: `1px solid ${alpha(ACCENT, 0.35)}`,
                  bgcolor: isDark ? alpha(ACCENT, 0.12) : alpha(ACCENT, 0.06),
                  '&:hover': { bgcolor: isDark ? alpha(ACCENT, 0.2) : alpha(ACCENT, 0.1) },
                }}
              >
                {lang.label}
              </Box>
            ))}
            <Box
              component={Link}
              to="/ide"
              sx={{
                px: 1.25,
                py: 0.5,
                borderRadius: 1.5,
                fontSize: '0.8rem',
                fontWeight: 650,
                textDecoration: 'none',
                color: muted,
                border: `1px solid ${border}`,
                '&:hover': { color: ACCENT, borderColor: alpha(ACCENT, 0.35) },
              }}
            >
              All languages →
            </Box>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default IdeLanguageSeoSection;
